import {
  CartDrawerCard as CartDrawerCardUI,
  getCartItemWeight,
  hideStrikethroughPrice,
  productPhotoMetadata,
} from '@iheartjane/menu';
import { useDrawerContext } from '@iheartjane/reefer';
import { formatCurrency } from '@iheartjane/utils';
import { useNavigate } from 'react-router';

import { useCartActions } from '@jane/monolith/components/cart/cartFacade/useCartActions';
import { useCustomerDispatch } from '@jane/monolith/customer/dispatch';
import { clearTouchedProduct } from '@jane/monolith/customer/redux/bundlePossibilities';
import { useMoengage } from '@jane/shared-ecomm/tracking';
import { productDetailLink as generatePdpLink } from '@jane/shared/components';
import { useGetStore } from '@jane/shared/data-access';
import {
  useNeedsSignInForEligibility,
  useShouldShowGold,
} from '@jane/shared/hooks';
/* eslint-disable-next-line no-restricted-imports */
import type { PendingCartProduct } from '@jane/shared/models';
import { useHeadlessApp } from '@jane/shared/providers';
import {
  calculateDiscountPercentage,
  postMessageToIframeParent,
} from '@jane/shared/util';

interface CartDrawerCardProps {
  bloomUserExperience?: boolean;
  headless?: boolean;
  product: PendingCartProduct;
  productDetailLink: string;
  productFullPrice: number;
  productPrice: number | null;
  storeId?: string | number;
}

export const CartDrawerCard = ({
  bloomUserExperience,
  headless,
  product,
  productDetailLink,
  productPrice,
  productFullPrice,
  storeId,
}: CartDrawerCardProps) => {
  const navigate = useNavigate();
  const dispatch = useCustomerDispatch();
  const { updateProduct, removeProduct: removeCartProduct } = useCartActions();
  const { data: store } = useGetStore(storeId);
  const { moeTrack } = useMoengage();
  const { options } = useHeadlessApp();

  const { onRequestClose } = useDrawerContext();

  const {
    amount,
    brand,
    brand_discount_label,
    brand_discount_title,
    brand_subtype,
    category,
    count,
    id,
    max_cart_quantity,
    name,
    price_id,
    special_title,
  } = product;
  const brandDiscountText = brand_discount_title || brand_discount_label;

  const shouldShowGold = useShouldShowGold({ store }) && !!brandDiscountText;

  const needsSignInForEligibility = useNeedsSignInForEligibility({
    cartProduct: product,
  });

  const maxCartQuantity = product[
    `max_cart_quantity_${price_id}` as keyof PendingCartProduct
  ]
    ? Number(
        product[`max_cart_quantity_${price_id}` as keyof PendingCartProduct]
      )
    : max_cart_quantity;

  const removeProduct = () => {
    removeCartProduct(product.id, product.price_id);
  };

  const editProductQuantity = (newCount: number) => {
    dispatch(clearTouchedProduct());
    if (count !== newCount) {
      if (headless) {
        const basePrice = Number(product[`price_${price_id}`]);
        const discountedPrice = Number(product[`discounted_price_${price_id}`]);
        postMessageToIframeParent({
          messageType: 'analyticsEvent',
          payload: {
            name: 'cartItemChangeCount',
            properties: {
              productId: id,
              priceId: price_id,
              count: newCount,
              previousCount: count,
              ...(basePrice > 0 && { price: basePrice }),
              ...(discountedPrice > 0 && { discountedPrice }),
            },
          },
        });
      }
      updateProduct(
        {
          product_id: id,
          count: newCount,
          price_id,
        },
        moeTrack
      );
    }
  };

  const { url: photoUrl } = productPhotoMetadata(product);

  const showWeight = price_id !== 'each' && !amount;
  const weightString = showWeight ? `/${getCartItemWeight(product)}` : '';

  const goToProductDetailPage = () => {
    if (headless) {
      postMessageToIframeParent({
        messageType: 'analyticsEvent',
        payload: {
          name: 'cartItemClickedInCheckout',
          properties: {
            productId: product.id,
            priceId: product.price_id,
            productCount: product.count,
            price: productPrice!,
          },
        },
      });

      postMessageToIframeParent({
        messageType: 'navigationEvent',
        payload: {
          name: 'navigateToProduct',
          link: generatePdpLink({
            product,
            appMode: bloomUserExperience ? 'bloom' : 'headless',
          }),
        },
      });
    }

    onRequestClose();
    navigate(productDetailLink);
  };

  const isDiscounted = productFullPrice !== productPrice;
  const fullPriceIsDiscounted =
    formatCurrency(productFullPrice * count) !==
    formatCurrency(productPrice! * count);

  const discountPercentage = calculateDiscountPercentage(
    productFullPrice,
    productPrice!
  );

  const specialBadgeColor = (
    options?.theme as { specialBadgeColor?: string } | undefined
  )?.specialBadgeColor;

  return (
    <CartDrawerCardUI
      bloomUserExperience={bloomUserExperience}
      brand={brand}
      brandDiscountText={brandDiscountText}
      brandSubtype={brand_subtype}
      category={category}
      count={count}
      discountPercentage={discountPercentage!}
      fullPriceIsDiscounted={fullPriceIsDiscounted}
      hideStrikethrough={hideStrikethroughPrice(store?.state || '')}
      isDiscounted={isDiscounted}
      maxCartQuantity={maxCartQuantity}
      name={name}
      needsSignInForEligibility={needsSignInForEligibility}
      onEditQuantity={editProductQuantity}
      onGoToProductDetailPage={goToProductDetailPage}
      onRemoveProduct={removeProduct}
      photoUrl={photoUrl}
      productFullPrice={productFullPrice}
      productPrice={productPrice}
      shouldShowGold={shouldShowGold && !!brandDiscountText}
      specialBadgeColor={specialBadgeColor}
      specialTitle={special_title}
      weightString={weightString}
    />
  );
};
